/**
 * Where to send the user after they sign in.
 *
 * The guard records the screen it bounced from in the router's location state
 * (memory only: nothing is written to the browser), and the login screen reads
 * it back. Both sides go through these two functions so the value is always an
 * in-app hash path: an absolute URL or a path outside the app would turn the
 * login screen into an open redirect, and `/login` itself would loop.
 */

import { PATHS } from '@/routes/paths';

export const LOGIN_PATH = '/login';

export interface ReturnState {
  readonly from: string;
}

export interface LocationLike {
  readonly pathname: string;
  readonly search?: string;
}

/** True for a hash path this app can navigate to and that is not the login screen. */
export function isReturnPath(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    value !== LOGIN_PATH &&
    !value.startsWith(`${LOGIN_PATH}?`)
  );
}

/** The state to hand `<Navigate to="/login">`, or `null` when there is nowhere worth returning to. */
export function returnStateFor(location: LocationLike): ReturnState | null {
  const from = `${location.pathname}${location.search ?? ''}`;
  return isReturnPath(from) ? { from } : null;
}

/** The route to go to after sign-in: the recorded one, else the Arena. */
export function readReturnPath(state: unknown): string {
  if (typeof state === 'object' && state !== null && 'from' in state) {
    const from: unknown = state.from;
    if (isReturnPath(from)) return from;
  }
  return PATHS.arena;
}
